import type { Map as MLMap } from 'maplibre-gl';
import type { LngLat } from '../shared/types';

export type CameraMode = 'chase' | 'overview' | 'free';

/**
 * カメラ制御。
 * - chase: 自車の後方から前方を見通す（スマホ縦向き向け）
 * - overview: 真上寄りの広域（iPad・横向き向け）
 * - free: ユーザーが地図を触ったら追従を止める。「追従」ボタンで復帰
 */
export class CameraController {
  mode: CameraMode = 'chase';
  private listeners = new Set<(m: CameraMode) => void>();

  constructor(private readonly map: MLMap) {
    // ユーザー操作（ドラッグ・ピンチ）で free に切り替える
    const toFree = (e: { originalEvent?: unknown }) => {
      if (e.originalEvent && this.mode !== 'free') this.setMode('free');
    };
    map.on('dragstart', toFree);
    map.on('rotatestart', toFree);
    map.on('pitchstart', toFree);
  }

  onChange(fn: (m: CameraMode) => void): void {
    this.listeners.add(fn);
  }

  setMode(m: CameraMode): void {
    this.mode = m;
    this.listeners.forEach((fn) => fn(m));
  }

  /** 画面サイズに応じた既定モード */
  defaultMode(): CameraMode {
    const w = window.innerWidth, h = window.innerHeight;
    return Math.min(w, h) >= 700 && w > h ? 'overview' : 'chase';
  }

  /** highway: 高速道路を走行中。引いて、角度をつけて遠くまで見通す */
  follow(position: LngLat, bearing: number, speedMps: number | null, highway = false): void {
    if (this.mode === 'free') return;
    const h = this.map.getContainer().clientHeight;
    if (this.mode === 'chase') {
      // 速いほど少し引いて遠くまで見せる
      const kmh = (speedMps ?? 0) * 3.6;
      const zoom = highway ? 14.8 : kmh > 80 ? 15.3 : kmh > 40 ? 16 : 16.8;
      this.map.easeTo({
        center: position,
        bearing,
        pitch: highway ? 65 : 60,
        zoom,
        // 自車を画面下寄りに（これ以上下げると下部パネルに隠れる）
        padding: { top: h * 0.35, bottom: 0, left: 0, right: 0 },
        duration: 900,
        easing: (t) => t,
      });
    } else {
      this.map.easeTo({ center: position, bearing: 0, pitch: 30, zoom: 14.5, padding: 0, duration: 900 });
    }
  }
}
